import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { assetUrl } from "../utils/api";
import {
  buildProfileGalleryUrls,
  ClickableImage,
  useImageLightbox,
} from "../components/ImageLightbox";
import VerificationBadges from "../components/VerificationBadges";
import { formatCityLabel, formatLocationDisplay } from "../utils/resolveRegion";

function isLocked(user, field) {
  return Boolean(user?.visibility?.locked_fields?.includes(field));
}

function fieldHint(user, field) {
  return user?.visibility?.field_hints?.[field] || "完善对应资料后可查看";
}

function FieldLine({ user, field, label, display }) {
  if (isLocked(user, field)) {
    return (
      <p className="text-amber-700/90">
        {label}：
        <span className="text-amber-600">🔒 {fieldHint(user, field)}</span>
      </p>
    );
  }
  return (
    <p>
      {label}：{display}
    </p>
  );
}

function TextBlock({ user, field, title, value }) {
  if (isLocked(user, field)) {
    return (
      <div>
        <h3 className="font-semibold mb-2">{title}</h3>
        <p className="text-amber-700 text-sm leading-7">
          🔒 {fieldHint(user, field)}
        </p>
      </div>
    );
  }
  return (
    <div>
      <h3 className="font-semibold mb-2">{title}</h3>
      <p className="text-gray-600">{value || "未填写"}</p>
    </div>
  );
}

export default function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUserId = Number(localStorage.getItem("user_id"));

  const [user, setUser] = useState(null);
  const [me, setMe] = useState(null);
  const [friendship, setFriendship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blockedReason, setBlockedReason] = useState("");
  const [nextStepOpen, setNextStepOpen] = useState(false);

  const galleryUrls = user
    ? buildProfileGalleryUrls(
        user.avatar,
        isLocked(user, "photos") ? [] : user.photos
      ).map((url) => assetUrl(url))
    : [];
  const { openAt, lightbox } = useImageLightbox(galleryUrls);
  const photoStartIndex = user?.avatar ? 1 : 0;

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setBlockedReason("");
      try {
        if (!currentUserId) {
          const userRes = await fetch(`/api/users/${id}`);
          const userData = await userRes.json();
          if (!userRes.ok) {
            setBlockedReason(userData.detail || "暂时无法查看该会员");
            return;
          }
          setUser(userData);
          setMe(null);
          setFriendship(null);
          return;
        }

        const [userRes, meRes] = await Promise.all([
          fetch(`/api/users/${id}?viewer_id=${currentUserId}`),
          fetch(`/api/profile/${currentUserId}`),
        ]);

        const userData = await userRes.json();
        if (!userRes.ok) {
          if (userRes.status === 403) {
            setBlockedReason(userData.detail || "无法查看该会员资料");
            setLoading(false);
            return;
          }
          alert(userData.detail || "获取资料失败");
          navigate(-1);
          return;
        }
        setUser(userData);

        if (meRes.ok) {
          setMe(await meRes.json());
        }

        if (Number(id) !== currentUserId) {
          const friendRes = await fetch(
            `/api/friendship-status?current_user_id=${currentUserId}&target_user_id=${id}`
          );
          if (friendRes.ok) {
            setFriendship(await friendRes.json());
          }

          fetch("/api/profile-views/record", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              viewer_id: currentUserId,
              target_user_id: Number(id),
              source: "profile",
            }),
          }).catch(() => {});
        } else {
          setFriendship({ is_self: true });
        }
      } catch (err) {
        console.error(err);
        alert("获取资料失败");
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [id, currentUserId, navigate, location.pathname, location.search]);

  const profileReady = Boolean(me?.profile_completed);
  const questionnaireReady = Boolean(me?.has_questionnaire);
  const canAddFriend = profileReady && questionnaireReady;
  const lockedCount = user?.visibility?.locked_fields?.length || 0;

  const promptCompleteBeforeFriend = () => {
    const missingLabels = me?.missing_required_labels || [];
    const missingText =
      missingLabels.length > 0
        ? `还缺：${missingLabels.join("、")}。\n\n`
        : "";

    if (!profileReady && !questionnaireReady) {
      const go = window.confirm(
        `${missingText}加好友前请先完善资料并完成 AI 测评。\n\n是否前往完善？`
      );
      if (go) navigate("/center/profile");
      return;
    }
    if (!profileReady) {
      const go = window.confirm(
        `${missingText || "请先完善个人资料后再加好友。\n\n"}是否前往完善资料？`
      );
      if (go) navigate("/center/profile");
      return;
    }
    if (!questionnaireReady) {
      const go = window.confirm(
        "请先完成 AI 测评后再加好友，系统才能智能匹配更合适的异性。是否前往测评？"
      );
      if (go) navigate("/questionnaire");
    }
  };

  const handleAddFriend = async () => {
    if (!canAddFriend) {
      promptCompleteBeforeFriend();
      return;
    }

    try {
      const res = await fetch("/api/friend-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from_user_id: currentUserId,
          to_user_id: user.id,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "发送失败");
        return;
      }

      setFriendship((prev) => ({
        ...prev,
        has_pending_request: true,
      }));
      setNextStepOpen(true);
    } catch (err) {
      console.error(err);
      alert("发送失败");
    }
  };

  const handleBlockUser = async () => {
    if (!user?.id) return;
    const ok = window.confirm(
      `确定拉黑 ${user.name} 吗？拉黑后将解除好友关系，双方无法互加好友与聊天。`
    );
    if (!ok) return;
    try {
      const res = await fetch("/api/friends/block", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_user_id: Number(currentUserId),
          target_user_id: user.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "拉黑失败");
        return;
      }
      alert(data.message || "已拉黑");
      setFriendship((prev) => ({
        ...prev,
        is_friend: false,
        has_pending_request: false,
        has_received_request: false,
        blocked_by_me: true,
      }));
    } catch (err) {
      console.error(err);
      alert("拉黑失败");
    }
  };

  const handleUnblockUser = async () => {
    if (!user?.id) return;
    const ok = window.confirm(`确定解除对 ${user.name} 的拉黑吗？`);
    if (!ok) return;
    try {
      const res = await fetch("/api/friends/unblock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_user_id: Number(currentUserId),
          target_user_id: user.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "解除失败");
        return;
      }
      alert(data.message || "已解除拉黑");
      setFriendship((prev) => ({
        ...prev,
        blocked_by_me: false,
      }));
    } catch (err) {
      console.error(err);
      alert("解除失败");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        加载中...
      </div>
    );
  }

  if (blockedReason) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow p-8 text-center space-y-4">
            <div className="text-5xl">🔒</div>
            <h2 className="text-xl font-bold text-gray-800">暂时无法查看</h2>
            <p className="text-gray-600">{blockedReason}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <button
                onClick={() => navigate("/center/profile")}
                className="px-5 py-3 rounded-xl bg-pink-500 text-white font-bold"
              >
                去完善资料
              </button>
              <button
                onClick={() => navigate(-1)}
                className="px-5 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold"
              >
                返回
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        加载中...
      </div>
    );
  }

  const showFriendActions = !friendship?.is_self;

  const avatarSrc = user.avatar
    ? assetUrl(user.avatar)
    : "https://via.placeholder.com/120";

  const locationText =
    formatLocationDisplay(
      user.province,
      user.city,
      isLocked(user, "district") ? "" : user.district,
      { separator: "", fallback: "未填写" }
    ) || "未填写";

  const cityLine = formatCityLabel(
    user.province,
    user.city,
    isLocked(user, "district") ? "" : user.district,
    "地区未填"
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
      <Header />

      <div className="flex-1 px-4 py-8">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow p-6 space-y-6">
          <div className="text-center">
            <ClickableImage
              src={avatarSrc}
              alt={user.name}
              className="w-28 h-28 rounded-full object-cover mx-auto mb-4"
              onOpen={() => openAt(0)}
              disabled={!galleryUrls.length}
            />
            <h2 className="text-2xl font-bold">{user.name}</h2>
            <p className="text-gray-500">
              {user.age}岁 · {cityLine}
            </p>
            <div className="mt-3">
              <VerificationBadges
                verification={user.verification}
                userId={user.id}
                userName={user.name}
                variant="detail"
              />
            </div>
          </div>

          {user.visibility?.mode === "reciprocal" && lockedCount > 0 ? (
            <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800 leading-7">
              <p>
                对等可见：你填写了哪些资料，就可以查看对方的对应资料。当前有{" "}
                {lockedCount} 项未解锁。
              </p>
              <p className="text-amber-700/90 text-xs mt-1">
                {user.visibility?.contact_note}
              </p>
              <button
                type="button"
                onClick={() => navigate("/center/profile")}
                className="mt-2 text-pink-600 font-medium hover:underline"
              >
                去完善资料解锁更多 →
              </button>
            </div>
          ) : null}

          {isLocked(user, "photos") ? (
            <div className="rounded-xl border border-dashed border-amber-200 bg-amber-50/50 p-4 text-sm text-amber-800">
              🔒 {fieldHint(user, "photos")}
            </div>
          ) : user.photos?.length > 0 ? (
            <div>
              <h3 className="font-semibold mb-3">生活照</h3>
              <div className="grid grid-cols-3 gap-3">
                {user.photos.map((photo, index) => (
                  <ClickableImage
                    key={index}
                    src={assetUrl(
                      typeof photo === "string"
                        ? photo
                        : photo?.image_url || photo?.url || ""
                    )}
                    alt={`生活照${index + 1}`}
                    className="w-full h-28 object-cover rounded-lg"
                    onOpen={() => openAt(photoStartIndex + index)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <p>性别：{user.gender || "未填写"}</p>
            <FieldLine
              user={user}
              field="marital_status"
              label="婚姻状态"
              display={user.marital_status || "未填写"}
            />
            <FieldLine
              user={user}
              field="education"
              label="学历"
              display={user.education || "未填写"}
            />
            <FieldLine
              user={user}
              field="job"
              label="职业"
              display={user.job || "未填写"}
            />
            <FieldLine
              user={user}
              field="height"
              label="身高"
              display={user.height ? `${user.height}cm` : "未填写"}
            />
            <FieldLine
              user={user}
              field="weight"
              label="体重"
              display={user.weight ? `${user.weight}kg` : "未填写"}
            />
            <FieldLine
              user={user}
              field="monthly_income"
              label="月收入"
              display={user.monthly_income || "未填写"}
            />
            <FieldLine
              user={user}
              field="housing_status"
              label="住房情况"
              display={user.housing_status || "未填写"}
            />
            <p className="col-span-2">
              所在地：{locationText}
              {isLocked(user, "district") ? (
                <span className="text-amber-600 text-xs ml-2">
                  （区县需完善后可见）
                </span>
              ) : null}
            </p>
          </div>

          <TextBlock user={user} field="bio" title="自我介绍" value={user.bio} />
          <TextBlock
            user={user}
            field="hobbies"
            title="兴趣爱好"
            value={user.hobbies}
          />
          <TextBlock
            user={user}
            field="partner_requirement"
            title="择偶要求"
            value={user.partner_requirement}
          />

          {user.preview_mode && !currentUserId && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 space-y-3">
              <p className="font-semibold text-rose-900">
                注册后即可查看完整资料，并获得AI匹配分析。
              </p>
              <p className="text-sm text-rose-800/80">
                {user.preview_note || "现在看到的是公开预览。完善资料后，还能分析你们哪里相似、哪里不同。"}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const redirect = encodeURIComponent(
                      `${location.pathname}${location.search}`
                    );
                    navigate(`/register?redirect=${redirect}`);
                  }}
                  className="py-3 rounded-lg bg-rose-600 text-white font-medium"
                >
                  立即注册
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const redirect = encodeURIComponent(
                      `${location.pathname}${location.search}`
                    );
                    navigate(`/login?redirect=${redirect}`);
                  }}
                  className="py-3 rounded-lg border border-rose-300 text-rose-700"
                >
                  已有账号登录
                </button>
              </div>
            </div>
          )}

          {showFriendActions && currentUserId && (
            <div className="space-y-3 pt-2 border-t">
              <button
                type="button"
                onClick={() => navigate(`/ai/match?target=${user.id}`)}
                className="w-full bg-gradient-to-r from-indigo-500 to-rose-500 text-white py-3 rounded-lg hover:opacity-95"
              >
                AI 匹配分析
              </button>
              <p className="text-xs text-center text-gray-500">
                看看你们为什么互相吸引，又可能在哪些地方产生分歧。
              </p>
              {!canAddFriend &&
                !friendship?.is_friend &&
                !friendship?.blocked_by_me &&
                !friendship?.blocked_me &&
                !friendship?.has_pending_request &&
                !friendship?.has_received_request && (
                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-800 space-y-2">
                    <p className="font-medium">加好友前请完成以下步骤：</p>
                    <ul className="list-disc list-inside space-y-1 text-amber-700">
                      {!profileReady && (
                        <li>
                          完善个人资料
                          {me?.missing_required_labels?.length
                            ? `（还缺：${me.missing_required_labels.join("、")}）`
                            : ""}
                        </li>
                      )}
                      {!questionnaireReady && <li>完成 AI 测评</li>}
                    </ul>
                    <p className="text-amber-600">
                      完成后系统将根据您的资料与测评结果，为您智能匹配更合适的异性。
                    </p>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {!profileReady && (
                        <button
                          type="button"
                          onClick={() => navigate("/center/profile")}
                          className="px-4 py-2 rounded-lg bg-amber-500 text-white text-sm hover:bg-amber-600"
                        >
                          去完善资料
                        </button>
                      )}
                      {!questionnaireReady && (
                        <button
                          type="button"
                          onClick={() => navigate("/questionnaire")}
                          className="px-4 py-2 rounded-lg border border-amber-400 text-amber-700 text-sm hover:bg-amber-100"
                        >
                          去完成测评
                        </button>
                      )}
                    </div>
                  </div>
                )}

              {friendship?.blocked_by_me ? (
                <div className="space-y-2">
                  <p className="text-center text-sm text-gray-500">你已拉黑对方</p>
                  <button
                    type="button"
                    onClick={handleUnblockUser}
                    className="w-full bg-gray-800 text-white py-3 rounded-lg hover:bg-gray-700"
                  >
                    解除拉黑
                  </button>
                </div>
              ) : friendship?.blocked_me ? (
                <button
                  type="button"
                  disabled
                  className="w-full bg-gray-200 text-gray-500 py-3 rounded-lg cursor-not-allowed"
                >
                  暂时无法互动
                </button>
              ) : friendship?.is_friend ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() =>
                      navigate("/chat", {
                        state: { user: { id: user.id, name: user.name } },
                      })
                    }
                    className="w-full bg-pink-500 text-white py-3 rounded-lg hover:bg-pink-600"
                  >
                    💬 和 TA 聊天
                  </button>
                  <button
                    type="button"
                    onClick={handleBlockUser}
                    className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50"
                  >
                    拉黑
                  </button>
                </div>
              ) : friendship?.has_pending_request ? (
                <div className="space-y-3">
                  <button
                    type="button"
                    disabled
                    className="w-full bg-gray-200 text-gray-500 py-3 rounded-lg cursor-not-allowed"
                  >
                    已发送好友申请
                  </button>
                  <p className="text-center text-sm text-gray-500">
                    对方通过后即可开始聊天。
                  </p>
                  <button
                    type="button"
                    disabled
                    className="w-full border border-gray-200 text-gray-400 py-3 rounded-lg cursor-not-allowed"
                  >
                    💬 聊天（等待对方通过）
                  </button>
                  <button
                    type="button"
                    onClick={() => setNextStepOpen(true)}
                    className="w-full border border-rose-200 text-rose-700 py-3 rounded-lg hover:bg-rose-50"
                  >
                    接下来做什么？
                  </button>
                  <button
                    type="button"
                    onClick={handleBlockUser}
                    className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50"
                  >
                    拉黑
                  </button>
                </div>
              ) : friendship?.has_received_request ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => navigate("/center/inbox")}
                    className="w-full bg-pink-500 text-white py-3 rounded-lg hover:bg-pink-600"
                  >
                    去收件箱处理申请
                  </button>
                  <button
                    type="button"
                    onClick={handleBlockUser}
                    className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50"
                  >
                    拉黑
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={handleAddFriend}
                    className="w-full border-2 border-pink-500 text-pink-600 py-3 rounded-lg font-medium hover:bg-pink-50"
                  >
                    加为好友
                  </button>
                  <button
                    type="button"
                    onClick={handleBlockUser}
                    className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50"
                  >
                    拉黑
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {lightbox}
      {nextStepOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/45 flex items-end sm:items-center justify-center p-4"
          onClick={() => setNextStepOpen(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-5 shadow-xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-stone-900">好友申请已发送</h3>
              <p className="text-sm text-stone-500 leading-relaxed">
                对方通过后即可开始聊天。等待的时候，你可以：
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setNextStepOpen(false);
                navigate("/discover");
              }}
              className="w-full rounded-xl border border-rose-200 bg-rose-50 p-4 text-left hover:border-rose-400"
            >
              <p className="font-semibold text-stone-900 text-sm">继续浏览其他人</p>
              <p className="text-xs text-stone-500 mt-1">
                再看看，也许还有一个更适合认识的人。
              </p>
            </button>
            <button
              type="button"
              onClick={() => {
                setNextStepOpen(false);
                navigate("/ai/hub/advisor");
              }}
              className="w-full rounded-xl border border-amber-200 bg-amber-50 p-4 text-left hover:border-amber-400"
            >
              <p className="font-semibold text-stone-900 text-sm">学习如何与异性沟通</p>
              <p className="text-xs text-stone-500 mt-1">
                不知道怎么开口？先学习如何自然地认识一个人。
              </p>
            </button>
            <button
              type="button"
              onClick={() => setNextStepOpen(false)}
              className="w-full py-2.5 text-sm text-stone-500"
            >
              先留在这里
            </button>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
